// Клиент источника данных для веб-табло AntScore.
//
// Режимы:
//   1. LIVE  — поллинг GET /api/matches/shared/:shareId (Strapi), интервал 1.5с.
//   2. DEMO  — проигрывание встроенного/переданного pointLog без backend.
//
// Выбор режима по query-параметрам страницы:
//   ?share=<shareId>   → LIVE (с автоматическим fallback в DEMO при ошибке сети)
//   ?demo              → DEMO со встроенным сценарием
//   ?demo=<seq>        → DEMO с произвольной последовательностью, напр. ?demo=AABBAAAA
//   (без параметров)   → DEMO (превью по умолчанию)

import { deserialize, type Match, type Team } from './engine';
import { extractShareIdFromInput } from './view';

export type Mode = 'live' | 'demo';

// База API для LIVE-режима. Запекается на build-time из VITE_API_URL
// (`--build-arg VITE_API_URL=https://api.antscore.dev.pl.webant.ru` в CI).
// Фолбэк — пустая строка: относительный путь для dev через vite-proxy.
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '');

export interface MatchSource {
  mode: Mode;
  shareId?: string;
  /** Подписка: колбэк вызывается при каждом обновлении match. */
  subscribe(onUpdate: (match: Match) => void): void;
  /** Остановить поллинг/демо-таймеры. */
  stop(): void;
}

const POLL_INTERVAL_MS = 1500;
const DEMO_STEP_MS = 1200;

// Встроенный демо-сценарий: матч best-of-3, доходит до тайбрейка в первом сете.
// Последовательность победителей очков (A — синие, B — красные).
const DEMO_SEQ =
  // сет 1: размен геймов до 5-5, затем 6-6 и тайбрейк, A берёт тайбрейк 7-5
  'AAAA BBBB AAAA BBBB AAAA BBBB AAAA BBBB AAAA BBBB ' + // 5-5
  'AAAA BBBB ' + // 6-6 → тайбрейк
  'ABABABABABAB' + // тайбрейк 6-6
  'AA' + // A берёт тайбрейк (по 2 в запасе) → 7-6 сет 1 за A
  // сет 2: A быстро ведёт, текущий розыгрыш с deuce для красивого табло
  'AAAA BBBB AAAA AABB';

const DEMO_CONFIG = { firstServer: 'A' as Team };

const DEMO_TEAMS = {
  A: { name: 'Синие', players: ['Алекс', 'Мария'] },
  B: { name: 'Красные', players: ['Иван', 'Ольга'] },
};

function parseSeq(seq: string): Team[] {
  return seq
    .replace(/[^AB]/g, '')
    .split('')
    .map((ch) => ch as Team);
}

/** Распарсить query-строку и определить источник данных. */
export function resolveSource(search: string = window.location.search): MatchSource {
  const params = new URLSearchParams(search);
  // ?code=A7K32 — вход по коду матча (WEB2 → WEB1). Нормализуется в shareId.
  const codeRaw = params.get('code');
  const codeShare = codeRaw ? extractShareIdFromInput(codeRaw) : null;
  const share = codeShare ?? params.get('share') ?? params.get('shareId') ?? extractPathShareId();

  // ?demo принудительно включает демо. Без share и без demo — превью-демо.
  if (params.has('demo') || !share) {
    const raw = params.get('demo');
    const seq = raw && /[AB]/.test(raw) ? parseSeq(raw) : parseSeq(DEMO_SEQ);
    return new DemoSource(seq);
  }
  return new LiveSource(share);
}

// Поддержка пути вида /shared/<shareId> или /#<shareId>.
function extractPathShareId(): string | null {
  const hash = window.location.hash.replace(/^#/, '').trim();
  if (hash && /^[A-Za-z0-9_-]+$/.test(hash)) return hash;
  const m = window.location.pathname.match(/\/shared\/([A-Za-z0-9_-]+)/);
  return m ? m[1] : null;
}

/** LIVE: поллинг backend с fallback в демо при недоступности. */
class LiveSource implements MatchSource {
  mode: Mode = 'live';
  shareId: string;
  private timer: ReturnType<typeof setInterval> | null = null;
  private fallback: DemoSource | null = null;
  private lastSerialized = '';

  constructor(shareId: string) {
    this.shareId = shareId;
  }

  subscribe(onUpdate: (match: Match) => void): void {
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/matches/shared/${encodeURIComponent(this.shareId)}`, {
          headers: { Accept: 'application/json' },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const data = json.data ?? json;
        const match = deserialize(data);
        const ser = JSON.stringify(match.pointLog);
        if (ser !== this.lastSerialized) {
          this.lastSerialized = ser;
          onUpdate(match);
        }
      } catch (err) {
        // backend недоступен — переходим в демо-режим (превью)
        if (!this.fallback) {
          console.warn('[antscore-web] backend недоступен, демо-режим:', String(err));
          this.stop();
          this.mode = 'demo';
          this.fallback = new DemoSource(parseSeq(DEMO_SEQ));
          this.fallback.subscribe(onUpdate);
        }
      }
    };
    void poll();
    this.timer = setInterval(poll, POLL_INTERVAL_MS);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.fallback?.stop();
  }
}

/** DEMO: пошагово докидывает очки из последовательности. */
class DemoSource implements MatchSource {
  mode: Mode = 'demo';
  shareId = 'A7K32'; // демо-код для блока «Следить»
  private seq: Team[];
  private idx = 0;
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(seq: Team[]) {
    this.seq = seq;
  }

  subscribe(onUpdate: (match: Match) => void): void {
    const emit = () => {
      const match: Match = deserialize({
        config: DEMO_CONFIG,
        teams: DEMO_TEAMS,
        pointLog: this.seq.slice(0, this.idx),
      });
      onUpdate(match);
    };
    emit();
    this.timer = setInterval(() => {
      if (this.idx >= this.seq.length) {
        // зацикливаем демо для бесконечного превью
        this.idx = 0;
      } else {
        this.idx++;
      }
      emit();
    }, DEMO_STEP_MS);
  }

  stop(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}
